import { Button, Form } from 'antd';
import React, { useEffect, useState } from 'react';

import { ButtonGroup, Drawer, ErrorHandler } from '@common/components';
import { useQuery } from '@common/hooks';
import { ComplianceItemUpdateFormV2 } from '@onboarding/components';
import {
  ComplianceItemUpsertDrawerV2Query,
  ComplianceItemUpsertDrawerV2QueryType,
} from '@onboarding/graphql';

const refetchQueries = [
  'PaginatedWorksListItems',
  'CandidateAssignmentQueryComplianceTabQuery',
  'ComplianceItemDrawerQuery',
  'ComplianceItemDrawerFilesTabQuery',
  'ComplianceItemUpsertDrawerV2Query',
];

export function ComplianceItemUpsertDrawerV2({
  open,
  onClose,
  complianceItemId = '',
}: {
  open: boolean;
  onClose: () => void;
  complianceItemId?: string;
}) {
  const [form] = Form.useForm();
  const [formLoading, setFormLoading] = useState(false);

  const {
    data,
    loading: queryLoading,
    error,
    refetch,
  } = useQuery<ComplianceItemUpsertDrawerV2QueryType>(
    ComplianceItemUpsertDrawerV2Query,
    {
      skip: !complianceItemId,
      variables: { id: complianceItemId },
      fetchPolicy: 'no-cache',
    }
  );
  const loading = !data || queryLoading;

  useEffect(() => {
    form.resetFields();
  }, [form, data?.worksListItem.detailItem]);

  const complianceItem = data?.worksListItem;
  const hasDetailItem = complianceItem?.detailItem;

  if (error || !(loading || hasDetailItem)) {
    return (
      <ErrorHandler
        error={error}
        messageType="contextual"
        log="ComplianceItemUpsertDrawer"
      />
    );
  }

  function onCloseDrawer() {
    form.resetFields();
    onClose();
  }

  return (
    <Drawer
      className="compliance-item-upsert-drawer"
      open={open}
      onClose={onCloseDrawer}
      title={data ? `Edit ${data.worksListItem?.typeName}` : undefined}
      loading={loading || !hasDetailItem}
      footer={
        <ButtonGroup>
          <Button size="large" onClick={onCloseDrawer}>
            Back
          </Button>
          <Button
            type="primary"
            size="large"
            loading={formLoading}
            onClick={form.submit}
          >
            Update
          </Button>
        </ButtonGroup>
      }
    >
      {hasDetailItem && (
        <ComplianceItemUpdateFormV2
          form={form}
          refetchQueries={refetchQueries}
          complianceItem={complianceItem}
          onSubmit={() => setFormLoading(true)}
          onDeleted={refetch}
          onSuccess={() => {
            refetch();
            onCloseDrawer();
            setFormLoading(false);
          }}
          onError={() => setFormLoading(false)}
        />
      )}
    </Drawer>
  );
}
